import React, { useState } from 'react';

export const PieChart = ({ data, width = 400, height = 400 }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  // Make responsive
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const chartSize = isMobile ? Math.min(width, 300) : width;
  
  const centerX = chartSize / 2;
  const centerY = chartSize / 2;
  const radius = Math.min(chartSize, chartSize) / 2 - 40;
  
  // Calculate total
  const total = data.reduce((sum, item) => sum + item.value, 0);
  
  // Calculate slice angles
  let currentAngle = -90; // Start from top
  const slices = data.map((item, index) => {
    const percentage = (item.value / total) * 100;
    const sliceAngle = (item.value / total) * 360;
    
    const startAngle = currentAngle;
    const endAngle = currentAngle + sliceAngle;
    
    // Calculate path for slice
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    const x1 = centerX + radius * Math.cos(startRad);
    const y1 = centerY + radius * Math.sin(startRad);
    const x2 = centerX + radius * Math.cos(endRad);
    const y2 = centerY + radius * Math.sin(endRad);
    
    const largeArc = sliceAngle > 180 ? 1 : 0;
    
    const pathData = [
      `M ${centerX} ${centerY}`,
      `L ${x1} ${y1}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`,
      'Z'
    ].join(' ');
    
    // Calculate label position
    const labelAngle = (startAngle + sliceAngle / 2) * Math.PI / 180;
    const labelRadius = radius * 0.7;
    const labelX = centerX + labelRadius * Math.cos(labelAngle);
    const labelY = centerY + labelRadius * Math.sin(labelAngle);
    
    currentAngle = endAngle;
    
    return {
      path: pathData,
      percentage: percentage.toFixed(1),
      color: item.color,
      name: item.name,
      value: item.value,
      labelX,
      labelY,
      showLabel: percentage >= 5 // Only show label if >= 5%
    };
  });
  
  return (
    <div className="flex flex-col items-center gap-4 w-full overflow-x-hidden">
      <div className="w-full max-w-full flex justify-center">
        <svg 
          width="100%" 
          height={chartSize} 
          viewBox={`0 0 ${chartSize} ${chartSize}`}
          className="max-w-full"
          preserveAspectRatio="xMidYMid meet"
        >
          {slices.map((slice, index) => (
            <g key={index}>
              <path
                d={slice.path}
                fill={slice.color}
                stroke="white"
                strokeWidth="2"
                opacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.6}
                style={{
                  cursor: 'pointer',
                  transition: 'opacity 0.2s',
                  transform: hoveredIndex === index ? 'scale(1.02)' : 'scale(1)',
                  transformOrigin: `${centerX}px ${centerY}px`
                }}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                <title>{`${slice.name}: $${slice.value.toFixed(2)} (${slice.percentage}%)`}</title>
              </path>
              {slice.showLabel && (
                <text
                  x={slice.labelX}
                  y={slice.labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fill="white"
                  fontSize={isMobile ? "12" : "14"}
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  {slice.percentage}%
                </text>
              )}
            </g>
          ))}
        </svg>
      </div>
      
      {/* Legend */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 sm:gap-x-6 gap-y-2 w-full max-w-md px-2">
        {slices.map((slice, index) => (
          <div
            key={index}
            className="flex items-center gap-2 cursor-pointer"
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div
              className="w-4 h-4 rounded-full flex-shrink-0"
              style={{ backgroundColor: slice.color }}
            />
            <div className="flex flex-col">
              <span className="text-xs font-medium text-gray-700">{slice.name}</span>
              <span className="text-xs text-gray-500">${slice.value.toFixed(2)} ({slice.percentage}%)</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
